/**
 * Drives the computer opponent in single-player games.
 *
 * The core AI (`chooseAction`) is pure; this is the thin reactive glue that
 * watches the store and, whenever it becomes the AI seat's turn, picks a move
 * and dispatches it. The search runs in a Web Worker so even a deep "hard"
 * think never blocks the board; if workers are unavailable it falls back to a
 * synchronous pick on the main thread. The board stays the single source of
 * truth — the runner only ever calls `backend.dispatch`, the same path a human
 * move takes.
 */
import { createEffect, createSignal, onCleanup } from "solid-js";
import { unwrap } from "solid-js/store";
import {
  acceptDraw,
  chooseAction,
  declineDraw,
  evaluate,
  otherPlayer,
  type Action,
  type Difficulty,
  type PlayerId,
} from "../core";
import type { GameBackend } from "./store";
import type { AiRequest, AiResponse } from "./ai.worker";

/**
 * The AI accepts a draw offer unless it's meaningfully ahead. `evaluate` is in
 * pawn-ish units, so this is "within about a pawn and a half" — even or behind,
 * the computer shakes hands; clearly winning, it plays on.
 */
const DRAW_ACCEPT_MARGIN = 1.5;

export interface AiRunnerOptions {
  readonly difficulty?: Difficulty;
  /** Minimum visible "thinking" pause, in ms, before the AI commits a move. */
  readonly thinkMs?: number;
}

export interface AiRunner {
  /** True while the AI is deliberating (drives a "thinking…" indicator). */
  readonly thinking: () => boolean;
}

/** Search time budget per difficulty (the worker deepens until it expires). */
const BUDGET_MS: Readonly<Record<Difficulty, number>> = {
  easy: 50,
  normal: 250,
  hard: 1200,
};

export function createAiRunner(
  backend: GameBackend,
  seat: PlayerId,
  options: AiRunnerOptions = {},
): AiRunner {
  const difficulty = options.difficulty ?? "normal";
  const thinkMs = options.thinkMs ?? 550;
  const [thinking, setThinking] = createSignal(false);

  const worker = spawnWorker();
  let requestId = 0;

  /** Commit the worker's pick, but never before the minimum visible pause. */
  function commit(id: number, action: Action | null, startedAt: number): void {
    if (id !== requestId) return; // stale reply (turn changed under us)
    const finish = () => {
      if (id !== requestId) return;
      setThinking(false);
      if (
        action &&
        backend.state.phase === "playing" &&
        backend.state.turn === seat
      ) {
        backend.dispatch(action);
      }
    };
    const wait = Math.max(0, startedAt + thinkMs - performance.now());
    if (wait > 0) setTimeout(finish, wait);
    else finish();
  }

  createEffect(() => {
    // Track turn + ply so this re-runs after every applied action.
    const state = backend.state;
    const myTurn = state.phase === "playing" && state.turn === seat;
    void state.ply;

    // Hold off moving while a human draw offer is on the table — the draw
    // watcher below resolves it first (accept ends the game; decline clears the
    // offer, which re-runs this effect and the move proceeds).
    if (myTurn && state.drawOffer === otherPlayer(seat)) {
      setThinking(false);
      return;
    }

    if (!myTurn) {
      requestId++; // invalidate any in-flight think
      setThinking(false);
      return;
    }

    setThinking(true);
    const id = ++requestId;
    const startedAt = performance.now();

    if (worker) {
      const request: AiRequest = {
        id,
        // unwrap the Solid proxy so the state structured-clones cleanly.
        state: unwrap(backend.state),
        seat,
        difficulty,
        budgetMs: BUDGET_MS[difficulty],
      };
      worker.onmessage = (e: MessageEvent<AiResponse>) =>
        commit(e.data.id, e.data.action, startedAt);
      worker.postMessage(request);
      return;
    }

    // No Worker support: think synchronously after the visible pause instead.
    const timer = setTimeout(() => {
      if (id !== requestId) return;
      if (backend.state.phase !== "playing" || backend.state.turn !== seat) {
        setThinking(false);
        return;
      }
      const action = chooseAction(backend.state, seat, { difficulty, random: Math.random });
      setThinking(false);
      if (action) backend.dispatch(action);
    }, thinkMs);
    onCleanup(() => clearTimeout(timer));
  });

  // Respond to a draw the human has offered. This can happen on either side's
  // turn (an offer is off-turn), so it's a separate watcher from the move loop.
  // The computer pauses briefly, then accepts unless it judges itself ahead.
  const human = otherPlayer(seat);
  let drawTimer: ReturnType<typeof setTimeout> | undefined;
  createEffect(() => {
    const state = backend.state;
    const pending = state.phase === "playing" && state.drawOffer === human;
    if (!pending) {
      if (drawTimer !== undefined) {
        clearTimeout(drawTimer);
        drawTimer = undefined;
      }
      return;
    }
    if (drawTimer !== undefined) return; // a response is already scheduled
    drawTimer = setTimeout(() => {
      drawTimer = undefined;
      const s = backend.state;
      if (s.phase !== "playing" || s.drawOffer !== human) return;
      const score = evaluate(unwrap(s), seat);
      backend.dispatch(score <= DRAW_ACCEPT_MARGIN ? acceptDraw(seat) : declineDraw(seat));
    }, thinkMs);
  });

  onCleanup(() => {
    requestId++;
    if (drawTimer !== undefined) clearTimeout(drawTimer);
    worker?.terminate();
  });

  return { thinking };
}

function spawnWorker(): Worker | null {
  if (typeof Worker === "undefined") return null;
  try {
    return new Worker(new URL("./ai.worker.ts", import.meta.url), { type: "module" });
  } catch {
    return null;
  }
}
